import { useMemo, type MutableRefObject, type RefObject } from 'react';

import { useBetaFeature } from '../../../../core/beta';
import type { DatabaseEngineId } from '../../../../core/engines';
import {
  columnFilterUnavailableReason,
  resultStatement,
  type AddColumnToFilter,
} from '../../common/utils/addColumnToFilter';
import type { CompactVqeSource } from '../../visual-query-editor/compact/CompactVqePopover';
import { getVqeSchemaSnapshotForDocument } from '../../visual-query-editor/edit/useVqeEditWiring';
import { INLINE_DOC_PREFIX } from '../../visual-query-editor/inline/useInlineToolbarEditContext';
import { positionAtOffset } from '../../visual-query-editor/inline/inlineModelAdapter';
import { VQE_DOC_PREFIX } from '../../visual-query-editor/mode/QueryVisualModePanel';
import { useVqeStore } from '../../visual-query-editor/store/vqeStore';
import type { QueryEditorHandle } from '../components/QueryEditor';
import { postDraftRequest } from './useResultsAddToWhere';

type UseResultsAddToFilterParams = {
  tabId: string;
  engineId: DatabaseEngineId;
  /** The tab shows its Visual view. */
  isVisual: boolean;
  /** The Visual view's shown statement (its start offset). */
  visualStatementOffset: number | null;
  editorRef: RefObject<QueryEditorHandle | null>;
  /** Partial-run tracking ref owned by the host (kept in sync by executions). */
  lastPartialExecutedSqlRef: MutableRefObject<string | null>;
  /** Open the SQL view's compact Visual Query Editor on the caret's statement. */
  openCompactEdit: (source: CompactVqeSource) => void;
};

/**
 * The results grid's column-header "Add to Filter" for a Query tab: the
 * column's condition editor opens in the Visual Query Editor with Table
 * Explorer's default draft (default operator, no value).
 *
 * Visual view: the view's WHERE builder takes the request (a drilled subquery
 * is left first, as for "Add to WHERE"). SQL view: the caret moves onto the
 * statement the results came from (the one "Add to WHERE" edits) and the
 * compact popover opens there with the draft. Undefined — no item — while the
 * Visual Query Editor is turned off.
 */
export function useResultsAddToFilter({
  tabId,
  engineId,
  isVisual,
  visualStatementOffset,
  editorRef,
  lastPartialExecutedSqlRef,
  openCompactEdit,
}: UseResultsAddToFilterParams): AddColumnToFilter | undefined {
  const vqeEnabled = useBetaFeature('visual-query-editor');
  return useMemo(() => {
    if (!vqeEnabled) return undefined;
    const locate = () =>
      resultStatement(
        editorRef.current?.getValue() ?? '',
        engineId,
        isVisual ? { offset: visualStatementOffset } : { partialSql: lastPartialExecutedSqlRef.current },
      );
    const documentId = isVisual ? `${VQE_DOC_PREFIX}${tabId}` : `${INLINE_DOC_PREFIX}${tabId}`;
    return {
      unavailableReason: (column) =>
        columnFilterUnavailableReason(
          locate(),
          column,
          engineId,
          isVisual ? 'visual' : 'sql',
          getVqeSchemaSnapshotForDocument(documentId),
        ),
      open: (column) => {
        if (isVisual) {
          useVqeStore.getState().clearDrill(tabId);
          postDraftRequest({ documentId: `${VQE_DOC_PREFIX}${tabId}`, column, columnOnly: { view: 'visual' } }, 'not-editable');
          return;
        }
        const editor = editorRef.current;
        const result = locate();
        if (!editor || !result) return;
        // Reveal the statement's first line, then park the caret at its end
        // (before a `;`): the compact popover opens on the caret's statement.
        const text = result.buffer.parsedDocument.source;
        const from = positionAtOffset(text, result.start);
        const to = positionAtOffset(text, result.end);
        editor.revealLine(from.lineNumber, from.column);
        editor.setSelectionRange({ startLineNumber: to.lineNumber, startColumn: to.column, endLineNumber: to.lineNumber, endColumn: to.column });
        openCompactEdit('results-grid');
        postDraftRequest(
          { documentId: `${INLINE_DOC_PREFIX}${tabId}`, column, columnOnly: { view: 'sql' }, statementStart: result.start },
          'not-opened',
        );
      },
    };
  }, [vqeEnabled, tabId, engineId, isVisual, visualStatementOffset, editorRef, lastPartialExecutedSqlRef, openCompactEdit]);
}
